/**
 * Platform starter — metadata-driven module configs.
 *
 * Add a new module by appending an object with `route`, `ListProps`, and `FormProps`.
 * Sample: Departments (uses org-scoped department APIs + metadata filters).
 *
 * Note: CommonList uses filteredApiEndpoint as a literal path; org id is sent in the
 * request body / params by the list engine when present. Dedicated department pages
 * under Pages/Orgnaization/Department are also kept as a non-metadata reference.
 */

export const Configurations = [
  {
    route: "/sample-departments",
    ListProps: {
      title: "Departments (Sample)",
      // Resolved at runtime in pages that build the URL with org id; for CommonList
      // we use the filtered POST path pattern via custom fetch in dedicated pages.
      // This sample wires to SystemMetaData module `departments` through the
      // organization departments-filtered endpoint — prefer the dedicated
      // /organization/department routes for production use of this entity.
      apiEndpoint: "/api/organizations/0/department",
      filteredApiEndpoint: "",
      filterModule: "departments",
      useMetadataFilters: true,
      addRoute: "/organization/department",
      editRouteBase: "/organization/department/edit",
      tablePrimaryKey: "organization_department_id",
      tableName: "organization_departments",
      datagridKey: "organization_departments_grid",
      showAddButton: true,
      personalizeColumnKeys: [
        "department_name",
        "department_short_name",
        "department_mail_id",
        "description",
      ],
      config: {
        sort: { field: "department_name", direction: "asc" },
        defaultVisibleColumns: [
          "department_name",
          "department_short_name",
          "department_mail_id",
          "description",
        ],
        columnLabels: {
          department_name: "Department Name",
          department_short_name: "Short Name",
          department_mail_id: "Email",
          description: "Description",
        },
        columnWidths: {
          department_name: 220,
          department_short_name: 140,
          department_mail_id: 200,
          description: 280,
        },
      },
      mapResponseToRows: (data) =>
        (Array.isArray(data) ? data : []).map((item) => ({
          id: item.organization_department_id,
          department_name: item?.department_name || "-",
          department_short_name: item?.department_short_name || "-",
          department_mail_id: item?.department_mail_id || "-",
          description: item?.description || "-",
        })),
    },
    FormProps: {
      validateForm: (formData, setFormErrors) => {
        const errors = {};
        if (!formData.department_name?.trim()) {
          errors.department_name = "Department name is required.";
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
      },
      baseUrl: "/api/organizations/${org_id}/department",
      initialData: {
        department_name: "",
        department_short_name: "",
        department_mail_id: "",
        description: "",
      },
      successMessages: {
        add: "Department added!",
        update: "Department updated!",
      },
      redirectPath: "/organization/department",
      headerProps: {
        addMessage: "Add Department",
        updateMessage: "Update Department",
        homeLink: "/organization/department",
        homeText: "Departments",
      },
      Inputs: [
        {
          type: "Text",
          label: "Department Name *",
          mainKey: "department_name",
        },
        {
          type: "Text",
          label: "Short Name",
          mainKey: "department_short_name",
        },
        {
          type: "Text",
          label: "Email",
          mainKey: "department_mail_id",
        },
        {
          type: "MultiRow",
          label: "Description",
          mainKey: "description",
        },
      ],
      DataFetchers: [],
    },
  },
];

/** Removed EMS lead-activity embed — kept for Route.js import compatibility. */
export function getLeadActivityEmbedConfig() {
  return null;
}

/** Format a datetime value for lead/activity hints in CommonForm. */
export function formatDateTime(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (!s) return "";

  // datetime-local style: YYYY-MM-DDTHH:mm
  const localMatch = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2})?/
  );
  if (localMatch) {
    const [, y, mo, d, h, mi] = localMatch;
    return `${d}/${mo}/${y} ${h}:${mi}`;
  }

  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return s;

  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

/**
 * Ensure activity datetime is not before lead datetime.
 * Returns an error message string, or null when valid / incomplete.
 */
export function validateActivityDatetimeAgainstLead(activityDatetime, leadDatetime) {
  if (!activityDatetime || !leadDatetime) return null;

  const activity = new Date(activityDatetime);
  const lead = new Date(leadDatetime);
  if (Number.isNaN(activity.getTime()) || Number.isNaN(lead.getTime())) {
    return null;
  }

  if (activity.getTime() < lead.getTime()) {
    return "Activity date & time cannot be earlier than the lead date & time.";
  }
  return null;
}
